/** Content localization keys for CMS-backed certification training copy. */

export const CONTENT_KEY_PREFIX = "certification.content.";

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function isContentLocKey(value: string | null | undefined): boolean {
  return typeof value === "string" && value.startsWith(CONTENT_KEY_PREFIX);
}

/**
 * Compact UI label for a content localization key.
 * `certification.content.training.{uuid}.title` → `/training.[id].title`
 */
export function formatContentKeyDisplay(key: string): string {
  const rest = isContentLocKey(key) ? key.slice(CONTENT_KEY_PREFIX.length) : key;
  return `/${rest.replace(UUID_RE, "[id]")}`;
}

export function trainingTitleKey(trainingId: string): string {
  return `${CONTENT_KEY_PREFIX}training.${trainingId}.title`;
}

export function trainingSectionKey(trainingId: string): string {
  return `${CONTENT_KEY_PREFIX}training.${trainingId}.section`;
}

export function trainingTranscriptKey(trainingId: string): string {
  return `${CONTENT_KEY_PREFIX}training.${trainingId}.transcript`;
}

export function trainingVideoCommentaryKey(trainingId: string): string {
  return `${CONTENT_KEY_PREFIX}training.${trainingId}.videoCommentary`;
}

export function moduleTitleKey(moduleId: string): string {
  return `${CONTENT_KEY_PREFIX}module.${moduleId}.title`;
}

export function moduleDescriptionKey(moduleId: string): string {
  return `${CONTENT_KEY_PREFIX}module.${moduleId}.description`;
}

export function moduleCertificateHeaderTextKey(moduleId: string): string {
  return `${CONTENT_KEY_PREFIX}module.${moduleId}.certificateHeaderText`;
}

export function moduleCertificateCourseTextKey(moduleId: string): string {
  return `${CONTENT_KEY_PREFIX}module.${moduleId}.certificateCourseText`;
}

export function questionTextKey(questionId: string): string {
  return `${CONTENT_KEY_PREFIX}question.${questionId}.text`;
}

export function questionChoiceKey(questionId: string, choiceId: string): string {
  return `${CONTENT_KEY_PREFIX}question.${questionId}.choice.${choiceId}`;
}

/** Extract choice id from a choice key, if present. */
export function parseChoiceIdFromKey(key: string): string | null {
  const m = key.match(
    /^certification\.content\.question\.[0-9a-f-]{36}\.choice\.([0-9a-f-]{36})$/i,
  );
  return m?.[1] ?? null;
}

export function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export type LocFieldDraft = {
  key: string;
  valuesByLang: Record<string, string>;
  /** Languages whose values should be written on save. */
  dirtyLangs: string[];
};

export function emptyLocDraft(key: string, defaultLang = "en"): LocFieldDraft {
  return { key, valuesByLang: { [defaultLang]: "" }, dirtyLangs: [] };
}

export function locDraftFromStored(
  stored: string | null | undefined,
  proposedKey: string,
  defaultLang: string,
  definitionsForDefault?: Record<string, string>,
): LocFieldDraft {
  if (isContentLocKey(stored)) {
    const key = stored!;
    const fromDefs = definitionsForDefault?.[key] ?? "";
    return {
      key,
      valuesByLang: { [defaultLang]: fromDefs },
      dirtyLangs: [],
    };
  }
  const literal = stored?.trim() ?? "";
  return {
    key: proposedKey,
    valuesByLang: { [defaultLang]: literal },
    // Literals are treated as already-saved for the editor; migrate to keys on save via
    // ensureLocMigrationDirty (do not mark dirty here — that shows false "unsaved" state).
    dirtyLangs: [],
  };
}

/**
 * When the entity still stores a plain literal (not a content key), mark the default
 * language dirty so the next save writes localization definitions before swapping to the key.
 */
export function ensureLocMigrationDirty(
  draft: LocFieldDraft,
  stored: string | null | undefined,
  defaultLang: string,
): LocFieldDraft {
  if (isContentLocKey(stored)) return draft;
  const value = draft.valuesByLang[defaultLang] ?? "";
  if (!value.trim()) return draft;
  if (draft.dirtyLangs.includes(defaultLang)) return draft;
  return { ...draft, dirtyLangs: [...draft.dirtyLangs, defaultLang] };
}

export function setLocValue(
  draft: LocFieldDraft,
  lang: string,
  value: string,
): LocFieldDraft {
  const dirtyLangs = draft.dirtyLangs.includes(lang)
    ? draft.dirtyLangs
    : [...draft.dirtyLangs, lang];
  return {
    ...draft,
    valuesByLang: { ...draft.valuesByLang, [lang]: value },
    dirtyLangs,
  };
}

/** Collect dirty key→value maps grouped by language for batch upsert. */
export function collectDirtyUpdates(
  drafts: LocFieldDraft[],
): Array<{ lang: string; set: Record<string, string> }> {
  const byLang = new Map<string, Record<string, string>>();
  for (const draft of drafts) {
    for (const lang of draft.dirtyLangs) {
      const value = draft.valuesByLang[lang];
      if (value == null) continue;
      const set = byLang.get(lang) ?? {};
      set[draft.key] = value;
      byLang.set(lang, set);
    }
  }
  return [...byLang.entries()].map(([lang, set]) => ({ lang, set }));
}
